import type { TenantBrandingFonts } from '@/lib/types/tenant';

export interface FontSource {
  file: string;
  weight?: string;
  unicodeRange?: string;
}

export interface FontConfig {
  google?: string;
  local?: FontSource[];
  sizeAdjust?: string;
}

export const FONT_CONFIG: Record<string, FontConfig> = {
  // App default (loaded via next/font/google in app/layout.tsx). Registered here
  // so the embed loader skeleton can load the same font for tenants with no
  // brand font of their own.
  Geist: { google: 'Geist:wght@300;400;500;600;700' },
  Rubik: { google: 'Rubik:wght@300;400;500;600;700' },
  Assistant: {
    local: [
      {
        file: 'assistant-hebrew.woff2',
        weight: '200 700',
        unicodeRange: 'U+0307-0308, U+0590-05FF, U+200C-2010, U+20AA, U+25CC, U+FB1D-FB4F',
      },
      {
        file: 'assistant-latin-ext.woff2',
        weight: '200 700',
        unicodeRange:
          'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF',
      },
      {
        file: 'assistant-latin.woff2',
        weight: '200 700',
        unicodeRange:
          'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
      },
    ],
    sizeAdjust: '110%',
  },
  // Hebrew + Latin only — Ploni has no Arabic, and Cyrillic was a third of its weight.
  // `/fonts/*` is served immutable, so a re-cut must ship under a new filename.
  ploni: {
    local: [
      { file: 'ploni-regular.woff2', weight: '400' },
      { file: 'ploni-medium.woff2', weight: '500' },
      { file: 'ploni-semibold.woff2', weight: '600' },
      { file: 'ploni-bold.woff2', weight: '700' },
    ],
    sizeAdjust: '110%',
  },
  Simpler: {
    local: [
      { file: 'simpler-regular.woff2', weight: 'normal' },
      { file: 'simpler-bold.woff2', weight: 'bold' },
    ],
    sizeAdjust: '110%',
  },
  PingHL: {
    local: [
      { file: 'pinghl-light.woff2', weight: '300' },
      { file: 'pinghl-regular.woff2', weight: '400' },
      { file: 'pinghl-medium.woff2', weight: '500' },
      { file: 'pinghl-bold.woff2', weight: '700' },
      { file: 'pinghl-black.woff2', weight: '900' },
    ],
  },
  OpenSansHebrew: { google: 'Open+Sans:wght@300;400;600;700&subset=hebrew' },
  // T12's real brand webfont (self-hosted, variable 300–700). Latin only —
  // their non-Latin locales (he/ar/ru/zh) fall back to the system font, matching
  // t12-hotels.com's own behavior.
  Figtree: {
    local: [
      {
        file: 'figtree-latin.woff2',
        weight: '300 700',
        unicodeRange:
          'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
      },
      {
        file: 'figtree-latin-ext.woff2',
        weight: '300 700',
        unicodeRange:
          'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF',
      },
    ],
  },
};

export function getFontForLocale(fonts: TenantBrandingFonts, locale: string): string | undefined {
  return fonts[locale] ?? fonts.default;
}

export function getPrimaryFontName(fonts: TenantBrandingFonts, locale: string): string | undefined {
  return getFontForLocale(fonts, locale)?.split(',')[0]?.trim();
}

export function getFontUrls(
  fonts: TenantBrandingFonts,
  locale: string,
  display = 'block',
): string[] {
  const fontName = getPrimaryFontName(fonts, locale);
  if (!fontName) return [];

  const config = FONT_CONFIG[fontName];
  if (config?.google) {
    return [`https://fonts.googleapis.com/css2?family=${config.google}&display=${display}`];
  }
  return [];
}

/**
 * Prefix the widget injects onto every host-side `@font-face` family name so it
 * can never collide with a font the host page already declares under the bare
 * name (which would otherwise hijack the host's text — see widget loader). Mirrors
 * the `__<scope>_<hash>` convention `next/font` uses for the same reason.
 */
export const HOST_FONT_NAMESPACE = '__bazak_';

export function namespaceHostFontFamily(fontName: string): string {
  return `${HOST_FONT_NAMESPACE}${fontName}`;
}

function fontBasePath(fontName: string, basePathPrefix: string): string {
  return `${basePathPrefix}/fonts/${fontName.toLowerCase()}`;
}

/**
 * `sizeAdjust` as a multiplier, for callers that scale `font-size` instead of
 * declaring it on the face.
 *
 * `size-adjust` only applies to the face declaring it, so host-side it would cover our
 * copy of the brand font but not the host's — leaving the two rendering at different
 * sizes. Scaling `font-size` is equivalent for a single face and covers both.
 */
export function getFontSizeScale(fonts: TenantBrandingFonts, locale: string): number {
  const fontName = getPrimaryFontName(fonts, locale);
  const sizeAdjust = fontName ? FONT_CONFIG[fontName]?.sizeAdjust : undefined;
  if (!sizeAdjust) return 1;

  const percent = Number.parseFloat(sizeAdjust);
  return Number.isFinite(percent) && percent > 0 ? percent / 100 : 1;
}

/**
 * Builds `@font-face` rules for a tenant's local brand font.
 *
 * `basePathPrefix` is prepended to the default `/fonts/{name}` path so callers
 * outside the app origin (e.g. the embed widget served cross-origin) can point
 * the `src` urls at an absolute base.
 */
export function getLocalFontFaces({
  fonts,
  locale,
  basePathPrefix = '',
  familyName,
  fontDisplay = 'block',
  omitSizeAdjust = false,
}: {
  fonts: TenantBrandingFonts;
  locale: string;
  basePathPrefix?: string;
  /** Override the emitted `font-family` name (the `src` path still derives from the real font). */
  familyName?: string;
  fontDisplay?: string;
  /** Leave `size-adjust` off, for callers scaling `font-size` instead — see getFontSizeScale. */
  omitSizeAdjust?: boolean;
}): string | null {
  const fontName = getPrimaryFontName(fonts, locale);
  if (!fontName) return null;

  const config = FONT_CONFIG[fontName];
  if (!config?.local) return null;

  const family = familyName ?? fontName;
  const basePath = fontBasePath(fontName, basePathPrefix);
  const sizeAdjust =
    config.sizeAdjust && !omitSizeAdjust ? `\n      size-adjust: ${config.sizeAdjust};` : '';

  return config.local
    .map(({ file, weight, unicodeRange }) => {
      const unicodeRangeRule = unicodeRange ? `\n      unicode-range: ${unicodeRange};` : '';
      return `
    @font-face {
      font-family: "${family}";
      src: url("${basePath}/${file}") format("woff2");
      font-weight: ${weight || 'normal'};
      font-style: normal;
      font-display: ${fontDisplay};${sizeAdjust}${unicodeRangeRule}
    }`;
    })
    .join('\n');
}
